/* eslint-disable no-console */
import { reactive, type App } from 'vue'
import createAuth0Client, {
  type Auth0Client,
  type Auth0ClientOptions,
} from '@auth0/auth0-spa-js'
import { jwtDecode } from 'jwt-decode'
import { getAppMetadata } from '@/plan/metadata/metadata'
import authService, { NO_TOKEN_FOUND } from '@/api/authService'
import AuditService from '@/services/audit/audit.service'
import { Audit, Auth } from '@workspaces/types'
import { getCarto3ApiTokenFromLocalStorage } from '@/helpers/testing/testing.helper'
import BrowserEnvironmentResolver from '@/helpers/environment.helper'
import { AuthManager } from '@workspaces/services'
import BrowserCartoAuthorizer from '@/auth/carto-auth'
import { initializeUserPermissionManager } from '@/helpers/auth-browser.helper'
import CartoMetricEvents from '@/services/carto-metric-event.service'
import { subscribeToComments } from '@/helpers/comment.helper'
import { subscribeToPlans } from '@/helpers/plans.helper'

interface UseAuth0Options {
  redirectUri?: string
  clientId?: string
  [key: string]: unknown
}

const maxLoginRetries = 3
let loginRetries = 0

/** Creates the reactive Auth0 wrapper (replaces the Vue 2 `new Vue({...})` store). */
function createAuth0Instance(redirectUri: string, options: UseAuth0Options) {
  return reactive({
    loading: false,
    isUserAuthenticated: false,
    user: '' as string,
    // populated by init(); never read before then
    auth0Client: null as unknown as Auth0Client,
    error: null as unknown,
    accessTokenCartoV3: 'Waitting for token to be set',
    userCountries: [] as number[],
    role: 'unset' as string,
    cartoUser: 'unset',
    accountId: 'unset',

    /** Handles the callback when logging in using a redirect */
    async handleRedirectCallback(url: string) {
      if (loginRetries >= maxLoginRetries) {
        console.error(
          'Max login retries reached, loggin out and cleanning all application data',
        )
        this.auth0Client.logout()
        authService.removeAllApplicationData()
      }
      loginRetries++

      this.loading = true
      try {
        // If the user is returning to the app after   authentication..
        // handle the redirect and retrieve tokens
        await this.auth0Client.handleRedirectCallback(url)
        this.error = null
      } catch (e) {
        this.error = e
      } finally {
        // Initialize our internal authentication state
        this.isUserAuthenticated = await this.auth0Client.isAuthenticated()
        const token = await this.auth0Client.getTokenSilently()
        console.debug('🟧 Auth0: User token', { token })
        if (authService.isWrongToken(token)) {
          authService.setFirstLogin(true)
          console.debug(
            '🟧 Auth0: User does not has organization, assigning organization...',
            { token },
          )
          await authService.joinInviteUserToOrganization(token)
          console.debug('🟧 Auth0: Logging out and redirecting to login...')
          this.auth0Client.logout()

          authService.removeAllApplicationData()
          this.auth0Client.loginWithRedirect()
          // eslint-disable-next-line no-unsafe-finally
          return
        }
        // Audit event to register login
        this.setAccessTokenCartoV3(token)
        const isFirstLogin = authService.isFirstLogin()
        const authManager = AuthManager.getInstance()
        authManager.init(new BrowserCartoAuthorizer())
        const type = isFirstLogin
          ? Audit.AuditEventType.LoginFirstTime
          : Audit.AuditEventType.Login
        AuditService.createEvent(
          getAppMetadata(),
          BrowserEnvironmentResolver.getInstance(),
          { type },
        )
        authService.setFirstLogin(false)
        CartoMetricEvents.sendLoginEvent(
          getAppMetadata(),
          BrowserEnvironmentResolver.getInstance(),
        )
        await initializeUserPermissionManager()

        subscribeToPlans(
          getAppMetadata(),
          BrowserEnvironmentResolver.getInstance(),
        )

        subscribeToComments(
          this.getUser(),
          getAppMetadata(),
          BrowserEnvironmentResolver.getInstance(),
        )

        this.loading = false
      }
    },
    /** Authenticates the user using the redirect method */
    loginWithRedirect() {
      return this.auth0Client?.loginWithRedirect()
    },
    /** Returns all the claims present in the ID token */
    getIdTokenClaims(o?: Parameters<Auth0Client['getIdTokenClaims']>[0]) {
      return this.auth0Client?.getIdTokenClaims(o)
    },
    /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
    getTokenSilently(o?: Parameters<Auth0Client['getTokenSilently']>[0]) {
      return this.auth0Client?.getTokenSilently(o)
    },
    /** Logs the user out and removes their session on the authorization server */
    logout() {
      return this.auth0Client?.logout()
    },
    setAccessTokenCartoV3(token: string) {
      this.accessTokenCartoV3 = token
      if (token !== NO_TOKEN_FOUND) {
        const tokenInfo = jwtDecode<Record<string, string>>(token)
        this.user = tokenInfo[getAppMetadata().auth0_user_key]
        this.cartoUser = tokenInfo.sub
        this.accountId = tokenInfo[getAppMetadata().auth0_account_id_key]
        // deck.gl 9 removed setDefaultCredentials (no global CARTO credential
        // singleton). FI renders no CARTO deck layer, so nothing consumed the
        // token set here; CARTO data is fetched via AssetsService/AuthManager.
      }
    },
    getCartoUser() {
      return this.cartoUser
    },
    getAccountId() {
      return this.accountId
    },
    getAccessTokenCartoV3(): string | undefined {
      // TODO: Playwrigth - Find a better way of doing this
      const testingToken = getCarto3ApiTokenFromLocalStorage(
        BrowserEnvironmentResolver.getInstance(),
      )
      if (testingToken) {
        console.log('👿 Testing time, returning testing token', testingToken)
        return testingToken
      }

      const token = this.accessTokenCartoV3
      if (
        token === undefined ||
        token === NO_TOKEN_FOUND ||
        token === 'Waitting for token to be set'
      ) {
        console.log('No carto3 token found, redirecting to login')
        window.location.href = `${window.location.origin}/login`
      } else {
        return token
      }
    },
    setRole(role: Auth.Role) {
      this.role = role
    },
    isAdmin() {
      return this.role === Auth.Role.Admin
    },
    isPowerUser() {
      return this.role === Auth.Role.PowerUser
    },
    getUser() {
      return this.user
    },
    getRole() {
      return this.role
    },
    setUserCountries(countries: number[]) {
      this.userCountries = countries
    },
    getUserCountries() {
      return this.userCountries
    },

    /** Instantiate the SDK client (was the Vue 2 `created` lifecycle hook). */
    async init() {
      this.auth0Client = await createAuth0Client({
        ...options,
        client_id: options.clientId,
        redirect_uri: redirectUri,
      } as Auth0ClientOptions)
      console.debug('🟧 Auth0: Created client', this.auth0Client)
    },
  })
}

export type Auth0Instance = ReturnType<typeof createAuth0Instance>

let instance: Auth0Instance | undefined

/** Returns the current instance of the SDK.
 * Typed non-null: the instance is created at app startup (main.ts → useAuth0)
 * before any consumer reads it, matching the original (untyped) contract. */
export const getInstance = (): Auth0Instance => instance as Auth0Instance

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = (options: UseAuth0Options): Auth0Instance => {
  if (instance) return instance

  const { redirectUri = `${window.location.origin}/login`, ...rest } = options
  instance = createAuth0Instance(redirectUri, rest)
  // Kick off client creation (the former `created` hook was likewise not awaited).
  instance.init()

  return instance
}

// Vue 3 plugin to expose the wrapper object throughout the application.
export const Auth0Plugin = {
  install(app: App, options: UseAuth0Options) {
    app.config.globalProperties.$auth = useAuth0(options)
  },
}
